import { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel as CKFormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Text, Button } from '@mercator-gcp/grout';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';

const INVITE_BUTTON_TEXT = 'Invite';
const CANCEL_BUTTON_TEXT = 'Cancel';

const contentStyle = {
  maxWidth: '500px',
  width: 'fit-content',
  minWidth: '350px',
};

const titleStyle = {
  paddingBottom: 'padding_16',
};

const modalBodyStyle = {
  padding: 'padding_16',
  paddingBottom: '0px',
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'padding_16',
  paddingTop: 0,
  marginTop: 'padding_24',
};

const formFieldStyle = {
  marginTop: 'padding_8',
};

const labelStyle = {
  margin: 0,
  fontSize: '13px',
};

const FormLabel = ({ label }: { label: string }) => {
  return <CKFormLabel sx={labelStyle}>{label}</CKFormLabel>;
};

type InviteUsersModalProps = {
  isOpen: boolean;
  isCentered?: boolean;
  onInvite?: (clickText?: string) => void;
  onClose: () => void;
  onCancel: (clickText?: string) => void;
};

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
};

export const InviteNewUsersModal = ({
  isOpen,
  isCentered,
  onInvite,
  onClose,
  onCancel,
}: InviteUsersModalProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>();
  const { createUser } = useUserAccount();
  const { renderCustomToast: toast } = useToast();
  const [isCalling, setIsCalling] = useState(false);

  const handleFormSubmit = async (data: FormValues) => {
    setIsCalling(true);
    const createUserPayload = {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      job_title: data.jobTitle,
    };
    const response = await createUser(createUserPayload);
    if (response?.status === 400 || response?.status === 403) {
      setIsCalling(false);
      toast({
        title: 'Invitation Not Sent',
        description: response?.message,
        status: 'error',
      });
    } else {
      setIsCalling(false);
      toast({
        title: 'User invited!',
        status: 'success',
      });
      onInvite?.(INVITE_BUTTON_TEXT);
      reset({
        email: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
      });
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={isCentered} size="sm">
      <ModalOverlay />
      <ModalContent sx={contentStyle}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Box sx={modalBodyStyle}>
            <Text.Lead sx={titleStyle}>Invite new user</Text.Lead>
            <FormControl id="email" sx={formFieldStyle} isInvalid={!!errors.email}>
              <FormLabel label="Email" />
              <Controller
                name="email"
                defaultValue=""
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Please enter a valid email address',
                  },
                }}
                render={({ field }) => <Input type="email" {...field} />}
              />
              <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
            </FormControl>

            <FormControl
              id="firstName"
              sx={formFieldStyle}
              isInvalid={!!errors.firstName}
            >
              <FormLabel label="First Name" />
              <Controller
                name="firstName"
                defaultValue=""
                control={control}
                rules={{
                  required: 'First name is required',
                  minLength: {
                    value: 2,
                    message: 'First name must be at least 2 characters',
                  },
                }}
                render={({ field }) => <Input {...field} />}
              />
              <FormErrorMessage>
                {errors.firstName && errors.firstName.message}
              </FormErrorMessage>
            </FormControl>

            {/* Last Name Field */}
            <FormControl id="lastName" sx={formFieldStyle} isInvalid={!!errors.lastName}>
              <FormLabel label="Last Name" />
              <Controller
                name="lastName"
                defaultValue=""
                control={control}
                rules={{
                  required: 'Last name is required',
                  minLength: {
                    value: 2,
                    message: 'Last name must be at least 2 characters',
                  },
                }}
                render={({ field }) => <Input {...field} />}
              />
              <FormErrorMessage>
                {errors.lastName && errors.lastName.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl id="jobTitle" sx={formFieldStyle} isInvalid={!!errors.jobTitle}>
              <FormLabel label="Job Title" />
              <Controller
                name="jobTitle"
                defaultValue=""
                control={control}
                rules={{
                  required: 'Job title is required',
                  minLength: {
                    value: 2,
                    message: 'Job title must be at least 2 characters',
                  },
                }}
                render={({ field }) => <Input {...field} />}
              />
              <FormErrorMessage>
                {errors.jobTitle && errors.jobTitle.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
          <ModalFooter sx={modalFooterStyle}>
            <Button variant="functional" onClick={() => onCancel(CANCEL_BUTTON_TEXT)}>
              {CANCEL_BUTTON_TEXT}
            </Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={isCalling}
              isLoading={isCalling}
              aria-label="Submit user invite"
            >
              {INVITE_BUTTON_TEXT}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
